"use client";

import { useEffect, useState, startTransition } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Check, ArrowRight } from "lucide-react";
import { useCredits } from "@/hooks/use-credits";
import { useCheckout } from "@/hooks/use-checkout";
import { useSession, signIn } from "@/lib/auth/client";
import { getEffectiveCredits, isPromotionEnabled } from "@/lib/payments/client";
import { cn } from "@/lib/utils";
import {
  getDisplayCreditPlansClient,
  type CreditPlan,
  type CreditPackId,
} from "@/lib/payments/plans";
import {
  SHARED_PACK_INCLUDES,
  formatPriceUsd,
  getPackTagline,
} from "@/lib/payments/pricing-display";
import {
  FREE_PREVIEW_LIMIT,
  canUseFreePreviewForUser,
  formatFreePreviewCountLabel,
  freePreviewEligibilityFromCredits,
} from "@/lib/payments/free-preview-config";

function getMaxGenerationsFromPlan(plan: CreditPlan): number | null {
  if (plan.isFree) return FREE_PREVIEW_LIMIT;

  const hdLine = plan.valueBreakdown.find((l) => l.includes("HD previews"));
  if (hdLine) {
    const match = hdLine.match(/(\d+)\s*HD previews/i);
    if (match?.[1]) return Number(match[1]);
  }

  if (plan.estimatedGenerations) {
    const num = Number(plan.estimatedGenerations);
    if (!Number.isNaN(num)) return num;
  }

  return null;
}

export function LandingPricingSection() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const checkout = useCheckout();
  const { data: session } = useSession();
  const { data: credits } = useCredits();

  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    startTransition(() => {
      setIsMounted(true);
    });
  }, []);

  const isAuthenticated = isMounted ? !!session?.user : false;
  const freePreviewEligibility = freePreviewEligibilityFromCredits(credits);
  const freePreviewsRemaining = credits?.freePreviewsRemaining ?? FREE_PREVIEW_LIMIT;

  const canUseFreePreview =
    isAuthenticated && canUseFreePreviewForUser(freePreviewEligibility);

  const isStarterPromoEnabled = isPromotionEnabled("starter");
  const starterPromoCredits = getEffectiveCredits("starter");

  const allPlans = getDisplayCreditPlansClient({
    includeFree: true,
    isStarterPromoEnabled,
    starterPromoCredits,
  });

  const landingPlans = allPlans.filter((plan) =>
    ["free", "mini", "basic", "starter"].includes(plan.id)
  );

  useEffect(() => {
    const pack = searchParams.get("checkoutPackId") as CreditPackId | null;
    if (!pack) return;
    if (!session?.user) return;
    if (
      pack !== "mini" &&
      pack !== "basic" &&
      pack !== "starter" &&
      pack !== "pro" &&
      pack !== "power"
    )
      return;

    checkout.mutate(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { packId: pack as any, source: "landing_pricing" },
      {
        onSettled: () => {
          router.replace("/");
        },
      }
    );
  }, [checkout, router, searchParams, session?.user]);

  const startCheckout = (packId: CreditPackId) => {
    if (!isAuthenticated) {
      signIn.social({
        provider: "google",
        callbackURL: `/?checkoutPackId=${encodeURIComponent(packId)}`,
      });
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    checkout.mutate({ packId: packId as any, source: "landing_pricing" });
  };

  return (
    <section id="pricing" className="relative overflow-hidden bg-[#0A0C0F] px-6 py-24 md:py-32">
      <div className="pointer-events-none absolute left-1/2 top-0 h-96 w-[60rem] -translate-x-1/2 rounded-full bg-white/[0.025] blur-3xl" />
      <div className="mx-auto max-w-7xl">
        <div className="grid items-end gap-6 md:grid-cols-[1fr_auto]">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/35">
              Simple, flexible pricing
            </p>
            <h2 className="mt-4 max-w-2xl text-4xl font-bold tracking-tight text-white md:text-6xl">
              Credits that never expire
          </h2>
          </div>
          <p className="max-w-sm text-base leading-relaxed text-white/50 md:pb-2 md:text-right">
            No subscription. Top up when you need more.
          </p>
        </div>

        <div className="mt-12 rounded-[2rem] border border-white/10 bg-gradient-to-b from-white/[0.055] to-white/[0.02] p-3 shadow-2xl shadow-black/20 md:p-5">
          <div className="mb-5 flex flex-col justify-between gap-3 px-2 sm:flex-row sm:items-center">
            <div>
              <p className="text-sm font-semibold text-white">Choose your pack</p>
              <p className="mt-1 text-xs text-white/35">
                Start free, then buy only what you need.
              </p>
            </div>
            <span className="w-fit rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-xs font-medium text-white/45">
              One-time payment
            </span>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            {landingPlans.map((plan) => {
              const isPrimary = plan.id === "basic";
              const isFree = plan.isFree;
              const maxGenerations = getMaxGenerationsFromPlan(plan);
              const priceParts = formatPriceUsd(plan.priceUsd);
              const tagline = getPackTagline(plan.id, plan.description);
              const badgeLabel = plan.popular ? "Most popular" : plan.badge;

              return (
                <article
                  key={plan.id}
                  className={cn(
                    "relative flex h-full min-h-72 flex-col overflow-hidden rounded-[1.3rem] border p-5 transition-transform duration-300 hover:-translate-y-1",
                    isPrimary
                      ? "border-white/20 bg-white/[0.09] shadow-[0_8px_32px_rgba(0,0,0,0.35)]"
                      : "border-white/[0.07] bg-[#0D1014]"
                  )}
                >
                  {isPrimary ? (
                    <div className="pointer-events-none absolute -right-14 -top-20 h-40 w-40 rounded-full bg-white/[0.08] blur-2xl" />
                  ) : null}
                  <div className="mb-4 flex h-6 items-center">
                    {badgeLabel && (
                      <span
                        className={cn(
                          "inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
                          isPrimary
                            ? "bg-white text-[#0A0C0F]"
                            : "border border-white/10 bg-white/[0.06] text-white/65"
                        )}
                      >
                        {badgeLabel}
                      </span>
                    )}
                  </div>

                  <div className="mb-6 min-h-17">
                    <h3 className="text-base font-semibold leading-tight text-white">
                      {plan.name}
                    </h3>
                    <p className="mt-1.5 line-clamp-2 text-[13px] leading-snug text-white/40">
                      {tagline}
                    </p>
                  </div>

                  <div className="mb-1 flex h-10 items-end">
                    {isFree ? (
                      <span className="text-[2rem] font-bold leading-none tracking-tight text-white">
                        $0
                      </span>
                    ) : (
                      <div className="flex items-baseline gap-0.5">
                        <span className="text-[2rem] font-bold leading-none tracking-tight text-white">
                          ${priceParts.dollars}
                        </span>
                        <span className="pb-0.5 text-base font-medium text-white/40">
                          .{priceParts.cents}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="mb-8 h-5">
                    <p className="text-sm font-medium text-white/65">
                      {isFree
                        ? formatFreePreviewCountLabel(FREE_PREVIEW_LIMIT)
                        : `${maxGenerations ?? plan.credits} HD previews`}
                    </p>
                  </div>

                  <div className="mt-auto">
                    <Button
                      className={cn(
                        "h-11 w-full text-sm font-semibold",
                        isPrimary
                          ? "bg-white text-[#0A0C0F] hover:bg-[#E8EAED]"
                          : "border border-white/10 bg-white/[0.055] text-white hover:bg-white/[0.1]"
                      )}
                      onClick={() => {
                        if (isFree) {
                          if (!isAuthenticated) {
                            signIn.social({ provider: "google", callbackURL: "/app" });
                            return;
                          }

                          if (canUseFreePreview) {
                            router.push("/app");
                          } else {
                            router.push("/credits");
                          }
                          return;
                        }

                        startCheckout(plan.id as CreditPackId);
                      }}
                      disabled={checkout.isPending}
                    >
                      {isFree
                        ? !isAuthenticated
                          ? "Start for free"
                          : canUseFreePreview
                            ? freePreviewsRemaining === FREE_PREVIEW_LIMIT
                              ? "Use free preview"
                              : `${freePreviewsRemaining} free preview${freePreviewsRemaining === 1 ? "" : "s"} left`
                            : "Free previews used"
                        : plan.id === "basic"
                          ? `Get ${plan.credits} previews`
                          : `Choose ${plan.name}`}
                      <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                    </Button>
                  </div>
                </article>
              );
            })}
          </div>
          <div className="mt-5 text-center">
            <Link
              href="/credits"
              className="text-sm font-medium text-white/55 underline-offset-4 transition-colors hover:text-white hover:underline"
            >
              Compare larger packs for frequent use
            </Link>
          </div>
        </div>

        <div className="mt-8 rounded-2xl border border-white/[0.07] bg-white/[0.025] px-5 py-5">
          <p className="mb-3 text-center text-[11px] font-semibold uppercase tracking-wider text-[#9CA3AF]">
            Every paid pack includes
          </p>
          <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {SHARED_PACK_INCLUDES.map((item) => (
              <li key={item} className="flex items-center justify-center gap-2.5 text-sm text-white/65">
                <span className="flex h-6 w-6 items-center justify-center rounded-full border border-white/10 bg-white/[0.05]">
                  <Check className="h-3.5 w-3.5 shrink-0 text-white" aria-hidden />
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
